/* eslint-disable no-control-regex */
import { BaseSchema, number, string } from 'yup';

const URL_MATCH =
	/^https?:\/\/(?:www\.)?(?:(?:[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6})|localhost)\b(?:[-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;

const PHONE_MATCH = /^[\d*#+-]+$/;

export const MAX_CHARS_AUTHORIZATION = 24;
export const MAX_CHARS_TENANT_ID = 100;

export const numericIntervalValidationScheme = (
	max?: number,
	required?: boolean
) => {
	let validation = number()
		.typeError(intl.tr('validations.field.numeric'))
		.min(1, intl.tr('validations.field.minimum'))
		.required(intl.tr('validations.field.required'));
	if (max) {
		validation = validation.max(
			max,
			intl.tr('validations.field.maximum', { max })
		);
	}
	if (required) {
		validation = validation.required(intl.tr('validations.field.required'));
	}
	return validation;
};

export const emailValidationScheme = (required?: true) => {
	let validation = string().email(intl.tr('validations.field.email'));
	if (required) {
		validation = validation.required(intl.tr('validations.field.required'));
	}
	return validation;
};

export const requiredPhoneValidation = (required?: boolean) => {
	let validation = string()
		.test(
			'len',
			intl.tr('validations.field.phone.minimum'),
			(val: string | undefined) => !!val && val.length >= 4
		)
		.test(
			'phone',
			intl.tr('validations.field.phone.invalid'),
			(val: string | undefined) => !!val && !!PHONE_MATCH.test(val)
		);
	if (required) {
		validation = validation.required(intl.tr('validations.field.required'));
	}
	return validation;
};

export const urlValidationScheme = () => {
	const message = intl.tr('validations.field.url');
	return string().matches(URL_MATCH, { message });
};

export const stringRequiredValidationScheme = () =>
	string().required(intl.tr('validations.field.required'));

export const stringRequiredMaxCharsValidationScheme = (max: number) =>
	stringRequiredValidationScheme().max(
		max,
		intl.tr('validations.field.characters.maximum', { max })
	);

export const schemaRequiredWhenFieldEqualsValidationScheme = (
	schema: BaseSchema,
	fieldName: string,
	value: any
) =>
	schema.when(fieldName, {
		is: value,
		then: schema.required(intl.tr('validations.field.required')),
		otherwise: schema.notRequired()
	});

export const stringRequiredWhenFieldEqualsValidationScheme = (
	fieldName: string,
	value: any
) => schemaRequiredWhenFieldEqualsValidationScheme(string(), fieldName, value);

export const stringRequiredEqualsValidationScheme = (match: string) =>
	string()
		.test(
			'match',
			intl.tr('validations.field.must.match'),
			(val) => val === match
		)
		.required();

export const stringNotAnyValidationScheme = (
	required: boolean,
	match: string[],
	id: string
) => {
	const scheme = required ? stringRequiredValidationScheme() : string();
	return scheme.test({
		name: 'unique_and_not_empty',
		test: (val, { createError }) => {
			if (val === '')
				return createError({
					message: intl.tr('validations.field.required')
				});
			const lowerVal = val?.toLowerCase();
			if (match.some((v) => v.toLowerCase() === lowerVal)) {
				return createError({ message: intl.tr(id) });
			}
			return true;
		}
	});
};

export const validateRange = ({ min, max }: { min: number; max: number }) =>
	numericIntervalValidationScheme()
		.min(min, intl.tr('validations.field.range', { min, max }))
		.max(max, intl.tr('validations.field.range', { min, max }));
